import { useEffect, useRef, useState } from 'react';
import { Controller, useFormContext, get } from 'react-hook-form';

import { Box, SelectChangeEvent, styled } from '@mui/material';
import { TimeView } from '@mui/x-date-pickers-pro';
import moment, { Moment } from 'moment';

import { DatePicker, Select, TimePicker } from '@/components';
import { renderRequiredLabel } from '@/components/FormElements/RequiredFieldLabel';

const DateTimePickerSelectRoot = styled('div')(() => ({
  flex: 1,
  maxWidth: '100%',
  display: 'flex',
  flexDirection: 'column',
  '& .MuiFormControl-root': {
    width: '100%',
  },
}));

const FieldsContainer = styled('div')(() => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '2px',
  maxWidth: '100%',
  overflowX: 'hidden',
  whiteSpace: 'nowrap',
}));

export interface DateTimeSelectFieldProps {
  name: string;
  label: string;
  grid: { md: number };
  date: {
    label: string;
    rules?: {
      validate: (value: string, formValues: any) => boolean | string;
    };
    required?: boolean;
    clearable?: boolean;
  };
  time: {
    label: string;
    timeSteps: { hours: number; minutes: number };
    maxDateTime: moment.Moment;
    views: TimeView[];
    rules?: { required?: string };
  };
  select?: {
    label: string;
    name: string;
    items: { label: string; value: string }[];
    keepDate?: boolean;
  };
  format: string;
  cfsId: string | null;
}

export const DateTimeSelectField = (props: DateTimeSelectFieldProps) => {
  const { name, date, time, select, cfsId } = props;
  const {
    control,
    formState: { errors },
    setValue,
    watch,
    getValues,
  } = useFormContext();

  const [initialCfsDateTime, setInitialCfsDateTime] = useState<Moment | null>(
    null,
  );
  const [dateManuallyChanged, setDateManuallyChanged] =
    useState<boolean>(false);
  const [timeSet, setTimeSet] = useState<boolean>(false);
  const dateJustPicked = useRef(false);

  const mainDateTime = watch(name);
  const selectedOption = select ? watch(select.name) : null;

  const handleDateChange = (
    _: { value: Moment | null },
    value: Moment | null,
  ) => {
    setDateManuallyChanged(true);
    dateJustPicked.current = true;
    setTimeSet(false);
    setValue(name, value?.format('YYYY-MM-DD'), {
      shouldValidate: true,
    });

    if (select) setValue(select.name, null, { shouldValidate: false });
  };

  const handleTimeChange = (
    field: { value: Moment | null },
    value: Moment | null,
  ) => {
    if (!value || !value.isValid()) {
      return;
    }

    setTimeSet(true);

    const currentValue = field.value ? moment(field.value) : moment();
    const newDateTime = currentValue
      .hours(value.hours())
      .minutes(value.minutes());

    if (select) setValue(select.name, null, { shouldValidate: false });
    setValue(name, newDateTime.format('YYYY-MM-DD HH:mm'), {
      shouldValidate: true,
    });
  };

  const handleSelectChange = (
    event: SelectChangeEvent<unknown>,
    field: {
      onChange: any;
    },
  ) => {
    const value = event.target.value as string;
    field.onChange(value);
    setTimeSet(false);

    if (value) {
      if (select?.keepDate) {
        const currentValue = getValues(name);
        const dateOnly = currentValue
          ? moment(currentValue).format('YYYY-MM-DD')
          : null;
        setValue(name, dateOnly, { shouldValidate: true });
      } else {
        setValue(name, null, { shouldValidate: true });
      }
    }
  };

  useEffect(() => {
    if (!mainDateTime) return;

    const dateTime = moment(mainDateTime);
    if (!dateTime.isValid()) return;

    if (selectedOption) {
      if (select?.keepDate) {
        const dateOnly = moment(mainDateTime).format('YYYY-MM-DD');
        setValue(name, dateOnly, { shouldValidate: true });
      } else {
        setValue(name, null, { shouldValidate: true });
      }
    } else {
      const isFullDateTime =
        mainDateTime instanceof Date || String(mainDateTime).includes(':');
      const format = isFullDateTime ? 'YYYY-MM-DD HH:mm' : 'YYYY-MM-DD';
      setValue(name, dateTime.format(format), {
        shouldValidate: false,
      });
    }
  }, [mainDateTime, selectedOption, setValue, name]);

  // Detect external value changes (edit mode, CFS prefill) to set timeSet
  useEffect(() => {
    if (!mainDateTime) return;
    if (dateJustPicked.current) {
      dateJustPicked.current = false;
      return;
    }

    const isFullDateTime =
      mainDateTime instanceof Date || String(mainDateTime).includes(':');
    if (isFullDateTime) {
      setTimeSet(true);
    }
  }, [mainDateTime]);

  useEffect(() => {
    if (initialCfsDateTime) {
      return;
    }

    if (dateManuallyChanged) {
      setInitialCfsDateTime(null);
      return;
    }
    if (cfsId && mainDateTime) {
      setInitialCfsDateTime(moment(mainDateTime));
    }
  }, [cfsId, dateManuallyChanged, mainDateTime]);

  // Route the Controller error to the correct sub-field.
  const mainFieldError = get(errors, name)?.message as string | undefined;
  const hasDateButNoTime =
    !!mainDateTime && !String(mainDateTime).includes(':');
  // Hide date error only when the date IS filled but time is missing
  const dateError = hasDateButNoTime ? undefined : mainFieldError;
  // Show time error when required and (nothing entered OR date-only)
  const timeError =
    time.rules?.required &&
    !selectedOption &&
    mainFieldError === 'This field is required.' &&
    (!mainDateTime || !String(mainDateTime).includes(':'))
      ? 'This field is required.'
      : undefined;

  return (
    <DateTimePickerSelectRoot>
      <FieldsContainer>
        <Controller
          name={name}
          control={control}
          rules={date.rules}
          render={({ field }) => (
            <Box width="34%">
              <DatePicker
                value={field.value ? moment(field.value) : null}
                onChange={(value) => handleDateChange(field, value)}
                onNowClick={(value) => {
                  setTimeSet(true);
                  setValue(name, value.format('YYYY-MM-DD HH:mm'), {
                    shouldValidate: true,
                  });
                  if (select)
                    setValue(select.name, null, { shouldValidate: false });
                }}
                initialCfsDateTime={initialCfsDateTime}
                setDateManuallyChanged={setDateManuallyChanged}
                label={renderRequiredLabel(
                  date.label,
                  date.required && (!selectedOption || !!select?.keepDate),
                )}
                sx={{ borderRight: '0px' }}
                error={dateError}
              />
            </Box>
          )}
        />

        <Controller
          name={name}
          control={control}
          render={({ field }) => (
            <Box width="32%">
              <TimePicker
                {...time}
                label={renderRequiredLabel(
                  time.label,
                  !!time.rules?.required && !selectedOption,
                )}
                value={
                  selectedOption || !timeSet
                    ? null
                    : field.value && moment(field.value).isValid()
                    ? moment(field.value)
                    : null
                }
                onChange={(value) => handleTimeChange(field, value)}
                error={timeError}
              />
            </Box>
          )}
        />

        {select && (
          <Controller
            name={select.name}
            control={control}
            render={({ field }) => (
              <Box width="33%">
                <Select
                  {...field}
                  onChange={(e) => handleSelectChange(e, field)}
                  label={select.label}
                  sx={{ width: '100%' }}
                  items={select.items}
                  error={get(errors, select.name)?.message}
                />
              </Box>
            )}
          />
        )}
      </FieldsContainer>
    </DateTimePickerSelectRoot>
  );
};
